import { useRef } from "react";
import { motion } from "framer-motion";
import { Zap, Hammer, Cpu, Wrench, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { IMAGES, SITE } from "@/config/site";
import Section, { RevealItem } from "@/components/Section";
import FloatingObjects from "@/components/FloatingObjects";
import PageHead from "@/components/PageHead";
import { Link } from "react-router-dom";

const SERVICE_ICONS = [Zap, Hammer, Cpu, Wrench];
const SERVICE_IMAGES = [
  // electrical / electromechanical
  "https://images.unsplash.com/photo-1581094288338-2314dddb7ece?auto=format&fit=crop&w=1200&q=80",
  // fabrication / welding
  "https://images.unsplash.com/photo-1586333109867-812586269a58?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  // automation
  "https://images.unsplash.com/photo-1759159091682-3b98f4759367?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  // maintenance
  "https://images.unsplash.com/photo-1565793298595-6a879b1d9492?auto=format&fit=crop&w=1200&q=80",
];

function TiltCard({ children, testId }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg) translateZ(0)`;
  };
  const onLeave = () => {
    if (ref.current) ref.current.style.transform = "perspective(900px) rotateX(0) rotateY(0)";
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testId}
      className="transition-transform duration-200 will-change-transform"
      style={{ transformStyle: "preserve-3d" }}
    >
      {children}
    </div>
  );
}

export default function Services() {
  const { t, lang } = useLang();

  return (
    <>
      <PageHead customTitle={`${t.services.title} · ${SITE.brand.en}`} customDesc={t.services.subtitle} />

      <section className="relative pt-40 pb-16 overflow-hidden" data-testid="services-hero">
        <div className="absolute inset-0 -z-10">
          <img src={IMAGES.automation} alt="" className="w-full h-full object-cover opacity-25" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900 via-navy-900/85 to-navy-900" />
          <FloatingObjects density="low" />
        </div>
        <div className="container-pad">
          <div className="max-w-3xl">
            <div className="text-overline mb-4">{t.services.overline}</div>
            <h1 className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.05]">
              {t.services.title}
            </h1>
            <p className="mt-5 text-white/70 text-base md:text-lg max-w-2xl leading-relaxed">
              {t.services.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* 4 main service cards */}
      <Section testId="services-main-cards" className="!py-16">
        <div className="container-pad grid grid-cols-1 md:grid-cols-2 gap-6">
          {t.services.cards.map((c, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <RevealItem key={i}>
                <TiltCard testId={`service-card-${i}`}>
                  <div className="metallic-border-top group relative overflow-hidden border border-white/10 bg-navy-800/50 hover:border-gold-500/40 transition-all duration-500">
                    <div className="grid grid-cols-1 sm:grid-cols-5">
                      <div className="sm:col-span-2 relative h-48 sm:h-full min-h-[200px] overflow-hidden">
                        <img
                          src={SERVICE_IMAGES[i]}
                          alt=""
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-navy-900/85 via-navy-900/40 to-transparent" />
                      </div>
                      <div className="sm:col-span-3 p-7 md:p-9">
                        <div className="w-12 h-12 border border-white/10 bg-navy-900 flex items-center justify-center group-hover:border-gold-500/50 transition-colors">
                          <Icon className="w-5 h-5 text-gold-500" strokeWidth={1.6} />
                        </div>
                        <h3 className="mt-5 font-heading font-bold text-white text-xl md:text-2xl leading-snug">
                          {c.t}
                        </h3>
                        <p className="mt-3 text-sm md:text-[15px] text-white/65 leading-relaxed">{c.d}</p>
                        <div className="mt-6 inline-flex items-center gap-2 text-overline text-gold-500/80 group-hover:text-gold-500">
                          {lang === "ar" ? "اعرف المزيد" : "Capability"}
                          <ArrowRight className="w-3.5 h-3.5 rtl-flip" />
                        </div>
                      </div>
                    </div>
                  </div>
                </TiltCard>
              </RevealItem>
            );
          })}
        </div>
      </Section>

      {/* Mechanical Fabrication Capabilities */}
      <Section testId="services-capabilities" className="bg-navy-900/60">
        <div className="absolute inset-0 -z-10 grid-blueprint opacity-25" />
        <div className="container-pad">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
            <div>
              <RevealItem className="text-overline mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5 text-gold-500" /> {t.services.capOverline}
              </RevealItem>
              <RevealItem
                as="h2"
                className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-5xl text-white leading-tight max-w-2xl"
              >
                {t.services.capTitle}
              </RevealItem>
              <RevealItem className="mt-4 text-white/70 max-w-xl">{t.services.capSubtitle}</RevealItem>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {t.services.capItems.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: (i % 6) * 0.05 }}
                whileHover={{
                  rotateX: -4,
                  rotateY: 6,
                  scale: 1.02,
                  transition: { duration: 0.25 },
                }}
                style={{ transformStyle: "preserve-3d" }}
                data-testid={`capability-${i}`}
                className="relative p-5 bg-gradient-to-br from-steel-700/40 via-navy-800 to-navy-900 border border-white/10 hover:border-gold-500/40 cursor-default"
              >
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold-500/70 to-transparent opacity-60" />
                <div className="font-heading font-bold text-white text-sm md:text-base leading-snug">
                  {item}
                </div>
                <div className="mt-2 text-[10px] font-mono text-white/40 tracking-widest">
                  EAH · {String(i + 1).padStart(2, "0")}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-14">
            <Link to="/contact" className="btn-primary">
              {lang === "ar" ? "اطلب عرض سعر" : "Request a Quote"}
              <ArrowRight className="w-4 h-4 rtl-flip" />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
